import * as React from 'react';
import { ButtonLabelIcon } from './parts/ButtonLabelIcon';
import { ButtonLabel } from './parts/ButtonLabel';
import { createComponent, mouseFocusBehavior, focusRing, styled, } from '@workday/canvas-kit-react/common';
import { boxStyleFn } from '@workday/canvas-kit-react/layout';
import { borderRadius, space, spaceNumbers, type } from '@workday/canvas-kit-react/tokens';
function getIconColorSelectors({ canvas: { palette: { primary: themePrimary }, }, }, color, fill) {
    return {
        '&:focus span, &:hover span, & span': {
            '.wd-icon-fill, .wd-icon-accent, .wd-icon-accent2': {
                fill: color,
            },
            '.wd-icon-background': {
                fill: fill ? color : undefined,
            },
            '.wd-icon-background ~ .wd-icon-accent, .wd-icon-background ~ .wd-icon-accent2': {
                fill: fill
                    ? color === themePrimary.contrast
                        ? themePrimary.main
                        : themePrimary.contrast
                    : color,
            },
        },
    };
}
const ButtonContainer = styled('button')({
    ...type.levels.subtext.large,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    boxShadow: 'none',
    outline: 'none',
    fontWeight: type.properties.fontWeights.bold,
    lineHeight: 'normal',
    whiteSpace: 'nowrap',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    borderRadius: borderRadius.circle,
    position: 'relative',
    verticalAlign: 'middle',
    overflow: 'hidden',
    border: '1px solid transparent',
    background: 'transparent',
    transition: 'box-shadow 120ms linear, border 120ms linear, background-color 120ms linear, color 120ms linear',
    '&:disabled, &:disabled:active': {
        cursor: 'default',
        boxShadow: 'none',
        opacity: 0.4,
    },
    '&:hover:active': { transitionDuration: '40ms' },
    '& > *:first-of-type': {
        marginLeft: '0',
    },
    '& > *:last-of-type': {
        marginRight: 0,
    },
}, ({ size }) => {
    switch (size) {
        case 'large':
            return {
                ...type.levels.body.small,
                fontWeight: type.properties.fontWeights.bold,
                height: '48px',
                '& > * ': {
                    margin: `0 ${spaceNumbers.xxs / 2}px`,
                },
            };
        case 'medium':
        default:
            return {
                height: space.xl,
                '& > * ': {
                    margin: `0 ${spaceNumbers.xxs / 2}px`,
                },
            };
        case 'small':
            return {
                height: space.l,
                '& > * ': {
                    margin: `0 ${spaceNumbers.xxxs / 2}px`,
                },
            };
        case 'extraSmall':
            return {
                ...type.levels.subtext.medium,
                fontWeight: type.properties.fontWeights.bold,
                height: space.m,
                '& > * ': {
                    margin: `0 ${spaceNumbers.xxxs / 2}px`,
                },
            };
    }
}, ({ grow }) => grow && { width: '100%', maxWidth: '100%' }, ({ colors, fillIcon, theme }) => {
    if (!colors) {
        return;
    }
    const baseStyles = {
        ...(colors.default.icon && {
            '.wd-icon-fill, .wd-icon-accent, .wd-icon-accent2, .wd-icon-background': {
                transition: 'fill 120ms ease-in',
            },
            ...getIconColorSelectors(theme, colors.default.icon, fillIcon),
        }),
        backgroundColor: colors.default.background,
        borderColor: colors.default.border,
        color: colors.default.label,
    };
    const hoverStyles = {
        '&:hover': {
            ...(colors.hover.icon && getIconColorSelectors(theme, colors.hover.icon, fillIcon)),
            backgroundColor: colors.hover.background,
            borderColor: colors.hover.border,
            color: colors.hover.label,
        },
    };
    const activeStyles = {
        '&:active, &:focus:active, &:hover:active': {
            ...(colors.active.icon && getIconColorSelectors(theme, colors.active.icon, fillIcon)),
            backgroundColor: colors.active.background,
            borderColor: colors.active.border,
            color: colors.active.label,
        },
    };
    return {
        ...baseStyles,
        '&:focus': {
            ...(colors.focus.focusRing || focusRing({ separation: 2 }, theme)),
            ...(colors.focus.icon && getIconColorSelectors(theme, colors.focus.icon, fillIcon)),
            backgroundColor: colors.focus.background,
            borderColor: colors.focus.border,
            color: colors.focus.label,
        },
        ...activeStyles,
        ...hoverStyles,
        '&:disabled, &:active:disabled, &:focus:disabled, &:hover:disabled': {
            ...(colors.disabled.icon && getIconColorSelectors(theme, colors.disabled.icon, fillIcon)),
            backgroundColor: colors.disabled.background,
            borderColor: colors.disabled.border,
            color: colors.disabled.label,
            opacity: colors.disabled.opacity,
        },
        ...mouseFocusBehavior({
            '&:focus': {
                ...baseStyles,
                ...hoverStyles,
                ...activeStyles,
                outline: 'none',
                boxShadow: 'none',
                animation: 'none',
            },
        }),
    };
}, boxStyleFn);
export const getMinWidthStyles = (children, size) => {
    switch (size) {
        case 'large':
            return children ? '112px' : '48px';
        case 'medium':
            return children ? '96px' : space.xl;
        case 'small':
            return children ? space.xxxl : space.l;
        case 'extraSmall':
            return children ? 'auto' : space.m;
        default:
            return children ? '96px' : space.xl;
    }
};
export const getPaddingStyles = (children, size, icon, iconPosition) => {
    // In order to calculate the correct padding, we need to know its children
    // and what side the icon is on and if there's an icon provided
    if (!children) {
        // icon buttons do not have any padding
        return 0;
    }
    // If there are children AND an icon
    // 1. We check what side the icon is in
    // 2. Adjust padding to visually center the icon and text
    // If there is children (most likely just text)
    // 1. We keep the padding the same on both side
    switch (size) {
        case 'large':
            return icon
                ? iconPosition === 'start'
                    ? `0 ${space.l} 0 ${space.m}`
                    : `0 ${space.m} 0 ${space.l}`
                : `0 ${space.l}`;
        case 'medium':
            return icon
                ? iconPosition === 'start'
                    ? `0 ${space.m} 0 20px`
                    : `0 20px 0 ${space.m}`
                : `0 ${space.m}`;
        case 'small':
            return icon
                ? iconPosition === 'start'
                    ? `0 ${space.s} 0 ${space.xs}`
                    : `0 ${space.xs} 0 ${space.s}`
                : `0 ${space.s}`;
        case 'extraSmall':
            return icon
                ? iconPosition === 'start'
                    ? `0 ${space.xs} 0 ${space.xxs}`
                    : `0 ${space.xxs} 0 ${space.xs}`
                : `0 ${space.xs}`;
        default:
            return icon
                ? iconPosition === 'start'
                    ? `0 ${space.m} 0 20px`
                    : `0 20px 0 ${space.m}`
                : `0 ${space.m}`;
    }
};
export const BaseButton = createComponent('button')({
    displayName: 'Button',
    Component: ({ children, ...elemProps }, ref, Element) => {
        return (React.createElement(ButtonContainer, Object.assign({ as: Element, ref: ref, type: "button" }, elemProps), children));
    },
    subComponents: {
        Icon: ButtonLabelIcon,
        Label: ButtonLabel,
    },
});
